import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import CMSGallery from '../components/CMSGallery'

export default function TemperatureTransport() {
  return (
    <div className="space-y-0">
      <SEO
        title="Temperaturstyret transport (-20°C til +25°C) | Transport4U"
        description="Pålidelig temperaturstyret transport fra -20°C til +25°C. Vi håndterer frostvarer, kølevarer og kontrolleret varme med avancerede klimaanlæg og erfarne chauffører."
      />

      {/* Page Header */}
      <section className="bg-navy-950 text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-950 border border-sky-500/40 text-sky-300 text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-snowflake text-sky-400" aria-hidden="true" />
              <span>Temperaturstyret Logistik</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
              Temperaturstyret transport
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Transport4U sikrer en præcis og stabil temperatur under hele turen. Vi råder over moderne kølevogne med avancerede klimaanlæg til alle typer temperaturfølsomt gods.
            </p>
          </div>
        </div>
      </section>

      {/* Central Temperature Span Hero Graphic */}
      <section className="py-12 bg-sky-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-navy-950/80 rounded-2xl p-8 sm:p-10 border border-sky-500/30 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-2 text-center lg:text-left">
              <span className="text-xs uppercase font-bold tracking-widest text-sky-400">
                Præcis temperaturkontrol
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
                -20°C <span className="text-sky-400 font-light">til</span> +25°C
              </h2>
              <p className="text-slate-300 text-sm max-w-lg">
                Uanset om godset kræver dybfrost, stabil køling eller kontrolleret varme i vintermånederne, holder vi den præcise temperatur under hele turen.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
              <Link
                to="/kontakt"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm transition-colors whitespace-nowrap shadow-md"
              >
                <span>Bestil transport online</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
              <a
                href="mailto:transport4u.dk@gmail.com"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-750 text-white font-bold text-sm border border-slate-700 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope text-sky-400 text-xs" aria-hidden="true" />
                <span>Send e-mail</span>
              </a>
              <a
                href="tel:+4550352035"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-3.5 rounded-xl bg-navy-900 text-slate-300 hover:text-white text-xs font-semibold border border-slate-800 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-xs text-sky-400" aria-hidden="true" />
                <span>50 35 20 35</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Core Temperature Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-3 mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
              Vores løsninger
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Løsninger tilpasset dit godskrav
            </h2>
            <p className="text-slate-600 text-base">
              Vores topmoderne vognpark er opbygget specielt til at imødekomme de strengeste krav til temperaturstyret logistik.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 1. Frostvarer */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/80">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <i className="fa-solid fa-snowflake text-xl" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Frostvarer</h3>
                  <span className="text-xs font-semibold text-sky-600">Ned til -20°C dybfrost</span>
                </div>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                Vi transporterer dybfrosne varer med stabil frosttemperatur under hele kørslen. Ideelt til frosne fødevarer, kød, fisk og frostprodukter, hvor kølekæden aldrig må brydes.
              </p>
              <div className="mt-4 pt-4 border-t border-slate-200/60 text-xs text-slate-500 flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Avancerede klimaanlæg med høj køleeffekt</span>
              </div>
            </div>

            {/* 2. Kølevarer */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/80">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <i className="fa-solid fa-temperature-arrow-down text-xl" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Kølevarer</h3>
                  <span className="text-xs font-semibold text-sky-600">Kontrolleret friskkøling</span>
                </div>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                Pålidelig køletransport til mejeriprodukter, frisk kød, frugt og grønt samt catering. Vi sikrer ensartet temperaturfordeling i hele varerummet uanset udetemperaturen.
              </p>
              <div className="mt-4 pt-4 border-t border-slate-200/60 text-xs text-slate-500 flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Rene, hygiejniske og isolerede varerum</span>
              </div>
            </div>

            {/* 3. Temperaturfølsomt gods */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/80">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <i className="fa-solid fa-box-open text-xl" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Temperaturfølsomt gods</h3>
                  <span className="text-xs font-semibold text-sky-600">Præcision & omhyggelig håndtering</span>
                </div>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                Varer der hverken tåler for meget varme eller kulde. Vores erfarne chauffører har særlig fokus på korrekt lastning og skånsom behandling af sarte forsendelser.
              </p>
              <div className="mt-4 pt-4 border-t border-slate-200/60 text-xs text-slate-500 flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Godsforsikring dækker alle transporter</span>
              </div>
            </div>

            {/* 4. Gods med behov for kontrolleret varme */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/80">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <i className="fa-solid fa-temperature-arrow-up text-xl" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Kontrolleret varme</h3>
                  <span className="text-xs font-semibold text-sky-600">Op til +25°C frostsikring</span>
                </div>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                Når vinterkulden rammer, er visse væsker, kemikalier og fødevarer i fare for at frostskade. Vores klimaanlæg kan aktivt opvarme varerummet op til +25°C for at forhindre frost.
              </p>
              <div className="mt-4 pt-4 border-t border-slate-200/60 text-xs text-slate-500 flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Aktiv opvarmning i de kolde måneder</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facts Bar */}
      <section className="bg-slate-100 py-12 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            <div>
              <span className="text-2xl sm:text-3xl font-black text-slate-900 block">-20°C / +25°C</span>
              <span className="text-xs text-slate-600 font-medium mt-1 block">Temperaturspænd</span>
            </div>
            <div>
              <span className="text-2xl sm:text-3xl font-black text-slate-900 block">Op til 8</span>
              <span className="text-xs text-slate-600 font-medium mt-1 block">Europaller pr. køretøj</span>
            </div>
            <div>
              <span className="text-2xl sm:text-3xl font-black text-slate-900 block">Fra 1 kg</span>
              <span className="text-xs text-slate-600 font-medium mt-1 block">Samt ikke-standard gods</span>
            </div>
          </div>
        </div>
      </section>

      {/* CMS Gallery Section for Temperaturstyret Transport */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
              Udstyr & Kølerum
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Billeder af kølebiler og isolerede varerum
            </h2>
            <p className="text-slate-600 text-sm">
              Se billeder af vores tagmonterede klimaanlæg og rene, hygiejniske varerum.
            </p>
          </div>

          <CMSGallery gallerySlug="temperaturstyret-transport" />
        </div>
      </section>

      {/* Conversion Section */}
      <section className="py-16 bg-navy-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl font-black text-white">
            Skal vi sikre temperaturen på dit næste parti gods?
          </h2>
          <p className="text-slate-300 text-base max-w-2xl mx-auto">
            Uanset om du søger en fast kølekædepartner eller en enkelt forsendelse, står vi klar med nye biler og konkurrencedygtige priser.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm transition-colors shadow-md"
            >
              <span>Bestil transport online</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
            <a
              href="mailto:transport4u.dk@gmail.com"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-sm border border-slate-700 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-sky-400" aria-hidden="true" />
              <span>Send e-mail</span>
            </a>
            <a
              href="tel:+4550352035"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white text-xs font-semibold border border-slate-800 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span>Tlf: 50 35 20 35</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
